/**
 * OutcomePredictionBadge - Displays FOS outcome prediction as a badge
 *
 * Atomic level: Molecule
 * Used in: RecordDetail, FOSOpinionPanel
 *
 * Displays outcome prediction (Likely Upheld, Likely Not Upheld, Uncertain)
 * with color-coded styling based on prediction type.
 */

import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../../../lib/utils';
import type { OutcomePrediction } from '../../../../types/record';

const outcomeBadgeVariants = cva(
  'inline-flex items-center justify-center rounded border font-medium whitespace-nowrap',
  {
    variants: {
      prediction: {
        likely_upheld: 'bg-red-50 text-red-700 border-red-200',
        likely_not_upheld: 'bg-green-50 text-green-700 border-green-200',
        uncertain: 'bg-amber-50 text-amber-700 border-amber-200',
      },
      size: {
        sm: 'px-2 py-0.5 text-figma-xs',
        md: 'px-3 py-1.5 text-figma-sm',
        lg: 'px-3 py-1.5 text-figma-base',
      },
    },
    defaultVariants: {
      prediction: 'uncertain',
      size: 'md',
    },
  }
);

interface OutcomePredictionBadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof outcomeBadgeVariants> {
  prediction: OutcomePrediction;
}

/**
 * Get human-readable label for outcome prediction
 */
const getOutcomeLabel = (prediction: OutcomePrediction): string => {
  switch (prediction) {
    case 'likely_upheld':
      return 'Likely Upheld';
    case 'likely_not_upheld':
      return 'Likely Not Upheld';
    case 'uncertain':
      return 'Uncertain';
  }
};

const OutcomePredictionBadge = React.forwardRef<HTMLSpanElement, OutcomePredictionBadgeProps>(
  ({ className, prediction, size, ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={cn(outcomeBadgeVariants({ prediction, size }), className)}
        {...props}
      >
        {getOutcomeLabel(prediction)}
      </span>
    );
  }
);

OutcomePredictionBadge.displayName = 'OutcomePredictionBadge';

export { OutcomePredictionBadge, outcomeBadgeVariants };


